import { Button, Form, Input } from 'antd';
import TextEditor from 'common/components/form-elements/editor/editor';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { useRef, useState } from 'react';
import Quill from 'quill';
import { EditorProps } from 'common/components/form-elements/editor/editor.types';
import notificationService from 'common/services/notification-service/notification-service';
import { IEmailNotificationMessage } from 'common/services/notification-service/notification-service.types';
import { notification } from 'common/utils/notification';
import { isEmail } from 'common/helpers/email.helper';

export const EmailNotifications = () => {
  const [recipients, setRecipients] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [textWithoutHtml, setTextWithoutHtml] = useState<string>('');
  const [isEmailButtonDisabled, setIsEmailButtonDisabled] = useState<boolean>(false);

  const quillRef = useRef<Quill | null>(null);

  const recipientEmails = recipients
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
  const invalidRecipientEmails = recipientEmails.filter((email) => !isEmail(email));

  const handleTextChange = (content: string) => {
    setMessage(content);
  };

  const handleQuillChange = () => {
    if (quillRef.current) {
      const content = quillRef.current.root.innerHTML;

      setTextWithoutHtml(quillRef.current.getText().trim());
      handleTextChange(content);
    }
  };

  const EmailEditorProps: EditorProps = {
    readOnly: false,
    onTextChange: handleQuillChange,
    maxLength: 1000,
    toolbarOptions: {
      enableBold: true,
      enableItalic: true,
      enableUnderline: true,
      enableLink: true,
      enableList: true,
      enableClean: true,
    },
  };

  const handleSendNotification = async () => {
    if (invalidRecipientEmails.length > 0) {
      notification.error({
        message: `Invalid email address: ${invalidRecipientEmails.join(', ')}`,
        duration: 5,
      });
      return;
    }

    try {
      setIsEmailButtonDisabled(true);

      const emailMessage: IEmailNotificationMessage = {
        message: message,
        subject: subject,
        recipients: recipientEmails,
      };
      await notificationService.sendEmailMassNotification(emailMessage);

      setIsEmailButtonDisabled(false);
      notification.success({
        message: 'Email notification sent successfully!',
        duration: 5,
      });
    } catch (error: any) {
      const status = error.status;
      if (status === 422) {
        const serverMessage = error.details?.[0]?.errors?.[0];
        notification.error({
          message: serverMessage && serverMessage !== 'Invalid html content' ? serverMessage : 'Please remove formatting',
          duration: 5,
        });
      } else {
        console.error('Failed to send email notification:', error);
      }
      setIsEmailButtonDisabled(false);
    }
  };

  return (
    <FormWrapper layout="vertical">
      <Form.Item
        label="Email Recipients"
        htmlFor="recipients"
        className="mass-alerts-recipients"
        extra="Separate multiple email addresses with commas.">
        <p className="recipients-description">
          To send an email <b>only to specific recipients</b>, enter their email addresses below.
          <br />
          To send the email to <b>all residents</b>, leave this field blank.
        </p>
        <Input
          id="recipients"
          name="recipients"
          value={recipients}
          status={invalidRecipientEmails.length > 0 ? 'error' : undefined}
          onChange={(e) => setRecipients(e.target.value)}
        />
      </Form.Item>
      <Form.Item label="Email Subject">
        <Input showCount maxLength={50} name="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Form.Item>
      <Form.Item label="Email Message">
        <div>
          <TextEditor ref={quillRef} {...EmailEditorProps} />
        </div>
      </Form.Item>
      <Form.Item>
        <div className="btn-container">
          <Button
            type="primary"
            htmlType="submit"
            onClick={handleSendNotification}
            disabled={textWithoutHtml.length === 0 || subject.length === 0 || isEmailButtonDisabled}>
            Send Email
          </Button>
          <p>*Click only once</p>
        </div>
      </Form.Item>
    </FormWrapper>
  );
};
